// /health — system health snapshot. Three sections:
//   1. Summary cards (recon last-7 status mix, freeze count 30d, error rate 24h)
//   2. Queue depths (deferred banner + 4 stub cards)
//   3. Provider availability (last 1h success rate per provider)

import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../lib/providers.js';
import {
  HEALTH_PROVIDERS_QUERY_KEY,
  HEALTH_SUMMARY_QUERY_KEY,
  platformApi,
  type ProviderAvailability,
} from '../../lib/api/platform.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';

export function HealthPage() {
  const client = useApiClient();
  const summaryQ = useQuery({
    queryKey: HEALTH_SUMMARY_QUERY_KEY,
    queryFn: () => platformApi.healthSummary(client),
  });
  const providersQ = useQuery({
    queryKey: HEALTH_PROVIDERS_QUERY_KEY,
    queryFn: () => platformApi.healthProviders(client),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="System health"
        description="Snapshot of reconciliation, freezes, error rate, queues, and provider availability. Time-series lives in Grafana."
      />

      {summaryQ.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : summaryQ.isError ? (
        <ErrorRetry
          title="Failed to load summary"
          error={summaryQ.error}
          onRetry={() => {
            void summaryQ.refetch();
          }}
        />
      ) : (
        <SummarySection data={summaryQ.data} />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Provider availability (last 1h)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {providersQ.isPending ? (
            <div className="space-y-2 px-4 py-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : providersQ.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load providers"
                error={providersQ.error}
                onRetry={() => {
                  void providersQ.refetch();
                }}
              />
            </div>
          ) : providersQ.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No traffic in the last hour"
                description="Provider stats only appear once chats run."
              />
            </div>
          ) : (
            <ProviderTable rows={providersQ.data.rows} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummarySection({
  data,
}: {
  data: {
    reconciliationLast7: { green: number; driftWarn: number; driftAlert: number; error: number };
    freezeCount30d: number;
    errorActionsLast24h: number;
    queues: { name: string; waiting: number; active: number; available: boolean }[];
  };
}) {
  const reconAlerted =
    data.reconciliationLast7.driftAlert + data.reconciliationLast7.error > 0;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className={reconAlerted ? 'border-destructive/40' : ''}>
          <CardContent className="space-y-1 py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Reconciliation (last 7d)
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {data.reconciliationLast7.green}
              <span className="text-xs font-normal text-muted-foreground"> green</span>
            </div>
            <div className="text-xs text-muted-foreground">
              warn {data.reconciliationLast7.driftWarn} · alert{' '}
              {data.reconciliationLast7.driftAlert} · error {data.reconciliationLast7.error}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-1 py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Freeze count (30d)
            </div>
            <div className="text-2xl font-semibold tabular-nums">{data.freezeCount30d}</div>
            <div className="text-xs text-muted-foreground">
              Cost-anomaly + manual freezes
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-1 py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Error actions (24h)
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {data.errorActionsLast24h}
            </div>
            <div className="text-xs text-muted-foreground">
              audit_log entries with failure-shaped actions
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Queues (BullMQ)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
            <strong>Partial signal:</strong> queue counters live in Redis; surfacing
            them here requires a sample-counter or metrics tap. The cards below
            mirror the layout the wired version will use; for time-series, use
            the Grafana dashboard linked from{' '}
            <a
              href="https://grafana.cortexlayer.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              grafana.cortexlayer.dev
            </a>
            .
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {data.queues.map((q) => (
              <div
                key={q.name}
                className="rounded-md border p-3 opacity-60"
              >
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {q.name}
                </div>
                <div className="mt-1 text-xl font-semibold tabular-nums">—</div>
                <div className="text-[10px] text-muted-foreground">
                  counter not wired
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function ProviderTable({ rows }: { rows: readonly ProviderAvailability[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Provider</TableHead>
          <TableHead className="text-right">Calls</TableHead>
          <TableHead className="text-right">Successful</TableHead>
          <TableHead className="text-right">Success rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const pct = r.successRate * 100;
          const variant: 'default' | 'destructive' | 'secondary' =
            pct === 100 ? 'default' : pct >= 95 ? 'secondary' : 'destructive';
          return (
            <TableRow key={r.provider}>
              <TableCell className="font-mono text-sm">{r.provider}</TableCell>
              <TableCell className="text-right font-mono text-sm">
                {r.totalCalls.toLocaleString()}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {r.successfulCalls.toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                <Badge variant={variant}>{pct.toFixed(1)}%</Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
